// ════════════════════════════════════════════════════════════════════════
//  Everything the app knows about the child at the keyboard.
//
//  Practice code, curriculum bundle, progress, and the orchestration between
//  them: sign in, merge the server's progress, save a finished stage, flush what
//  is queued, clear the device for the next child.
//
//  Extracted from App so the app shell only decides which screen to show. All
//  the rules that used to be buried in a 240-line component can now be checked
//  on their own.
// ════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import type { Bundle, StageResult } from "@otto/lehrgang";
import { BackendError, signIn, type BackendConfig, type Course } from "../lib/backend.js";
import { bundleBaseFrom, type Origin } from "../../features/content/lib/content.js";
import { useBundle } from "../../features/content/hooks/useBundle.js";
import { lockOf, openCourses, type CourseLock } from "../../features/courses/lib/lock.js";
import { gateFrom, OPEN, type Gate } from "../../features/lessons/lib/gate.js";
import type { Measurement } from "../../features/assessment/hooks/useAssessment.js";
import {
  measurementOf,
  pendingAssessment,
  type Phase,
} from "../../features/assessment/lib/assessment.js";
import {
  clearProgress,
  EMPTY,
  loadProgress,
  merge,
  replaceProgress,
  saveStage,
  type Progress,
} from "../../features/progress/lib/progress.js";
import {
  endSession,
  enqueue,
  flushQueue,
  readChosenCourse,
  readCode,
  readCourses,
  writeChosenCourse,
  writeCode,
  writeCourses,
} from "../../features/session/lib/session.js";

const CONFIG_FROM_ENV = bundleBaseFrom(import.meta.env as Record<string, string>);

export type Learner = {
  /** The practice code, or null in guest mode. */
  readonly code: string | null;
  /** Die Lehrgänge der Klasse. Im Gastmodus leer. */
  readonly courses: readonly Course[];
  /** Warum ein Lehrgang noch zu ist – null, wenn er offen ist. */
  readonly lockOf: (course: Course) => CourseLock | null;
  /** Der gewählte Lehrgang, oder null solange die Wahl aussteht. */
  readonly course: Course | null;
  /**
   * Es gibt mehr als einen Lehrgang und das Kind hat noch nicht gewählt.
   *
   * Bei genau einem Lehrgang ist das nie wahr – dann gibt es keine Auswahl, so
   * wie bisher. Eine Frage mit einer Antwort ist keine Frage.
   */
  readonly needsCourseChoice: boolean;
  readonly chooseCourse: (slug: string) => void;
  /** Zurück zur Auswahl, ohne den Code zu verlieren. */
  readonly switchCourse: () => void;
  readonly bundle: Bundle;
  /**
   * Wie die Klasse ihre Lektionen freigibt.
   *
   * Im Gastmodus immer `frei`: das Gatter ist eine Unterrichtsentscheidung, und
   * ohne Klasse gibt es niemanden, der sie getroffen hätte. Die Grenze des
   * Gastmodus (`GUEST_LESSONS`) steht daneben und ist etwas anderes.
   */
  readonly gate: Gate;
  /** Woher der Lehrgang kommt – für die Anzeige, damit ein Rückfall auffindbar ist. */
  readonly origin: Origin;
  readonly progress: Progress;
  readonly signingIn: boolean;
  readonly signInError: string | null;
  readonly signInWithCode: (entered: string) => Promise<void>;
  /** Store a finished stage locally and, with a code, on the server. */
  readonly remember: (result: StageResult) => void;
  /**
   * Eine Messung des Tests festhalten. Ohne Code bleibt sie auf dem Gerät und
   * damit auf dem Bildschirm – der Gastmodus macht keinen Serverkontakt.
   */
  readonly saveMeasurement: (phase: Phase, measurement: Measurement) => void;
  /**
   * Der Test, der jetzt ansteht – oder null.
   *
   * `blocking` heisst: die Lektionen warten. Das gilt nur für den Eingangstest
   * und nur mit Code. Im Gastmodus steht der Test als Angebot da und hält
   * niemanden auf – ein Kind, das nur ausprobiert, soll nicht erst gemessen
   * werden.
   */
  readonly assessment: {
    readonly phase: Phase;
    readonly blocking: boolean;
    /** Der Eingangswert, für den Vergleich am Ende. */
    readonly before: { readonly chars: number; readonly seconds: number } | null;
  } | null;
  /** Clear code, queue and progress — for the next child on a shared iPad. */
  readonly finish: () => void;
  readonly forgetProgress: () => void;
};

export function useLearner({
  config,
  fetchFn = fetch,
  bundleBaseUrl = CONFIG_FROM_ENV,
}: {
  config: BackendConfig | null;
  fetchFn?: typeof fetch;
  bundleBaseUrl?: string | null;
}): Learner {
  // A code in sessionStorage means this child is still at it (reload,
  // accidental back). Then do not ask for the code again.
  const [code, setCode] = useState<string | null>(readCode);
  // Beides aus dem sessionStorage: nach einem Neuladen ist der Code noch da, und
  // ohne die Lehrgänge dazu übte das Kind still mit der eingebackenen Fassung
  // weiter statt mit der seiner Klasse.
  const [courses, setCourses] = useState<readonly Course[]>(readCourses);
  const [chosen, setChosen] = useState<string | null>(readChosenCourse);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [progress, setProgress] = useState<Progress>(loadProgress);
  /**
   * Die Messung im Gastmodus. Sie bleibt im Arbeitsspeicher: ohne Code gibt es
   * kein Kind, dem sie gehört, und auf einem Koffer-iPad soll die Zahl des
   * vorherigen nicht beim nächsten stehen.
   */
  const [guestMeasured, setGuestMeasured] = useState<Measurement | null>(null);

  // Gewählt werden kann nur, was offen ist: ein Lehrgang, dessen Vorgänger noch
  // nicht zu Ende geübt ist, bleibt zu. Die gesperrten stehen trotzdem in
  // `courses` – die Auswahl zeigt sie blass, damit das Kind sieht, dass es
  // weitergeht, und woran es liegt.
  const open = openCourses(courses);

  // Bei genau einem offenen Lehrgang wird nicht gefragt. Das ist der heutige
  // Normalfall und soll sich für das Kind auch dann nicht ändern, wenn die
  // Plattform mehrere kennt.
  const course =
    (courses.length === 1 ? courses[0] : open.find((c) => c.slug === chosen)) ?? null;
  const version = course ? { slug: course.slug, version: course.version } : undefined;

  const { bundle, herkunft: origin } = useBundle(
    version ? { version, ...(bundleBaseUrl ? { baseUrl: bundleBaseUrl } : {}) } : {},
  );

  // Get rid of what is queued: on sign-in, when the Wi-Fi comes back, and when
  // the iPad wakes up.
  //
  // Without those two events an entry would only be retried on the next
  // finished stage — in a school network that can be half an hour later, and
  // closing the tab loses it.
  useEffect(() => {
    if (!config || code === null) return;

    const attempt = () => void flushQueue(config, fetchFn);
    attempt();

    const onVisible = () => {
      if (document.visibilityState === "visible") attempt();
    };
    window.addEventListener("online", attempt);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", attempt);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [code, config, fetchFn]);

  const signInWithCode = useCallback(
    async (entered: string) => {
      if (!config) {
        setSignInError(
          "Diese App ist ohne Code eingerichtet. Übe einfach ohne Code weiter.",
        );
        return;
      }
      setSigningIn(true);
      setSignInError(null);
      try {
        const result = await signIn(config, entered, fetchFn);
        writeCode(entered);
        setCode(entered);
        writeCourses(result.courses);
        setCourses(result.courses);
        // Die Wahl bleibt offen; bei genau einem Lehrgang fragt niemand danach.
        writeChosenCourse(null);
        setChosen(null);

        // Der Serverstand dieses Codes ersetzt den Gerätestand. Zusammenführen
        // mit localStorage würde auf einem geteilten iPad Sterne des vorherigen
        // Kindes in den nächsten Code hineintragen.
        let merged = EMPTY;
        for (const row of result.progress) {
          merged = merge(merged, {
            stageKey: row.stage_key,
            accuracy: row.accuracy,
            stars: Math.min(3, Math.max(1, row.stars)) as 1 | 2 | 3,
          });
        }
        setProgress(replaceProgress(merged));
      } catch (error) {
        if (error instanceof BackendError && error.kind === "rejected") {
          setSignInError(
            "Diesen Code kennen wir nicht. Schau nochmal auf dein Kärtchen.",
          );
        } else {
          setSignInError(
            "Das Netz macht gerade nicht mit. Du kannst ohne Code weiterüben.",
          );
        }
      } finally {
        setSigningIn(false);
      }
    },
    [config, fetchFn],
  );

  const remember = useCallback(
    (result: StageResult) => {
      setProgress(saveStage(result));
      if (!config || code === null) return;

      const stage = bundle.lessons
        .flatMap((lesson) =>
          lesson.stages.map((s) => ({ lessonKey: lesson.key, stageKey: s.key })),
        )
        .find((s) => s.stageKey === result.stageKey);
      if (!stage) return;

      // Queue first, then try to send. If the tab closes in this very second
      // the exercise is not lost.
      enqueue({
        kind: "progress",
        code,
        lessonKey: stage.lessonKey,
        stageKey: stage.stageKey,
        accuracy: result.accuracy,
        time: Date.now(),
      });
      void flushQueue(config, fetchFn);
    },
    [code, bundle, config, fetchFn],
  );

  /**
   * Eine Messung des Tests festhalten.
   *
   * Sie geht durch dieselbe Warteschlange wie eine geübte Stufe – und das ist
   * hier wichtiger als dort: der Server nimmt je Phase nur die ERSTE Messung,
   * ein verlorener Eintrag fehlt also für immer. Das Kind kann den Test nicht
   * einfach noch einmal machen.
   *
   * Ohne Code (Gastmodus) wird nichts gespeichert. Der Test läuft trotzdem, das
   * Ergebnis steht auf dem Bildschirm – und kein Byte verlässt das iPad.
   */
  const saveMeasurement = useCallback(
    (phase: Phase, measurement: Measurement) => {
      if (code === null) {
        setGuestMeasured(measurement);
        return;
      }

      setCourses((before) =>
        before.map((c) =>
          c.slug === course?.slug
            ? {
                ...c,
                tests: [
                  ...c.tests,
                  { phase, ...measurement, taken_at: new Date().toISOString() },
                ],
              }
            : c,
        ),
      );

      const testKey = course?.test ?? null;
      if (!config || code === null || testKey === null) return;

      enqueue({
        kind: "test",
        code,
        testKey,
        phase,
        chars: measurement.chars,
        seconds: measurement.seconds,
        accuracy: measurement.accuracy,
        time: Date.now(),
      });
      void flushQueue(config, fetchFn);
    },
    [code, config, course, fetchFn],
  );

  const chooseCourse = useCallback(
    (slug: string) => {
      // Ein gesperrter Lehrgang lässt sich nicht wählen, auch nicht an der
      // Karte vorbei. Die Karte ist deaktiviert; das hier ist die Regel.
      if (!open.some((c) => c.slug === slug)) return;
      writeChosenCourse(slug);
      setChosen(slug);
    },
    [open],
  );

  const switchCourse = useCallback(() => {
    writeChosenCourse(null);
    setChosen(null);
  }, []);

  const finish = useCallback(() => {
    endSession();
    setCode(null);
    setCourses([]);
    setChosen(null);
    setProgress(clearProgress());
  }, []);

  const forgetProgress = useCallback(() => setProgress(clearProgress()), []);

  const test = bundle.assessment ?? null;
  const phase =
    code === null ? (guestMeasured ? null : "vorher") : pendingAssessment(course);
  const assessment =
    test === null || phase === null
      ? null
      : {
          phase,
          blocking: code !== null && phase === "vorher",
          before: course ? measurementOf(course, "vorher") : null,
        };

  return {
    code,
    courses,
    lockOf: (candidate: Course) => lockOf(candidate, courses),
    course,
    needsCourseChoice: courses.length > 1 && course === null,
    chooseCourse,
    switchCourse,
    bundle,
    gate: code === null || course === null ? OPEN : gateFrom(course),
    origin,
    progress,
    signingIn,
    signInError,
    signInWithCode,
    remember,
    saveMeasurement,
    assessment,
    finish,
    forgetProgress,
  };
}
